// Tappable summary card used on hub pages (Insights, Advisor Setup, Advisor
// Plan). Visual: large emoji icon, bold title, 1-2 line muted description,
// optional inline metric, trailing chevron. min-h-[88px] keeps the tap target
// well above 44px (mobile UX audit rule).
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

interface Props {
  to: string;
  icon: string;
  title: string;
  description?: string;
  /** Right-aligned metric line (e.g. "$1,247.50"). Rendered tabular-nums. */
  metric?: ReactNode;
  /** Optional secondary metric line shown below the primary one. */
  metricSecondary?: ReactNode;
  /** Disabled state — renders a non-link card with reduced opacity. */
  disabled?: boolean;
}

export function HubTile({ to, icon, title, description, metric, metricSecondary, disabled }: Props) {
  const inner = (
    <div className="flex min-h-[88px] items-center gap-3 rounded-2xl border bg-card p-4 active:bg-muted/50">
      <span className="text-2xl leading-none" aria-hidden>
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        {/* 0.3.4: line-clamp-2 instead of truncate so long localized titles
            wrap to 2 lines on 375px mobile. The tile uses min-h-[88px] which
            already accommodates the 2-line case without breaking the grid.
            0.3.5: text-sm → text-base (16px) + still semibold so hub titles
            read as real callouts, not body-level fields. */}
        <div className="line-clamp-2 text-base font-semibold">{title}</div>
        {description && (
          <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{description}</div>
        )}
      </div>
      {(metric != null || metricSecondary != null) && (
        <div className="flex flex-col items-end gap-0.5 text-right">
          {metric != null && (
            <div className="text-sm font-semibold tabular-nums">{metric}</div>
          )}
          {metricSecondary != null && (
            <div className="text-xs text-muted-foreground tabular-nums">{metricSecondary}</div>
          )}
        </div>
      )}
      <span className="text-muted-foreground" aria-hidden>›</span>
    </div>
  );

  if (disabled) {
    return <div className="opacity-50">{inner}</div>;
  }
  return <Link to={to}>{inner}</Link>;
}
